import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import {
  BUY_FOR_STORAGE_WIZARD,
  SIGNIFYD_SESSION_ID,
  STATUS_NEW_ADDRESS_ADDED,
  UPGRADE_DELIVERY_ONLY_ACCOUNT,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import StepByStep from '../StepByStep';
import OrderSelection from './OrderSelection';
import OrderReview from './OrderReview';
import OrderConfirmation from './OrderConfirmation';
// import Status from '../Modals/Status';
import {
  selectBuyError,
  selectIsBuyStorageSuccessful,
  selectStorageProduct,
} from '../../store/selectors/buyStorageSelectors';
import Paragraph from '../Paragraph/Paragraph';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import BasicModal from '../BasicModal/BasicModal';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import {
  accountTypeKeys,
  accountTypes,
} from '../../util/enum/api/accountTypes';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import config from '../../config';

const BuyFlowSteps = [
  { children: OrderSelection },
  { children: OrderReview },
  { children: OrderConfirmation },
];

const BuyFlow = ({
  handleCloseModal,
  isModalOpen,
  // setWizardData,
  // wizardData,
  // handleBack,
  // handleNext,
  handleCloseModalOnBuy,
  // setCloseIconVisible,
  isProductPage,
  skipChooseLocation,
  isSegregated,
  tiers,
  counter,
  unity,
  isInstaVaultProduct,
}) => {
  const { t } = useTranslation();
  const settings = useSelector(selectSettings);
  const account = useSelector(getCurrentAccount);
  const isBuyStorageSuccessful = useSelector(selectIsBuyStorageSuccessful);
  const errorMessage = useSelector(selectBuyError);
  const storageProduct = useSelector(selectStorageProduct);
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const [wizardData, setWizardData] = useState(
    () => retrieveFromSessionStorage(WIZARD_DATA) || {},
  );
  const isPostPaidEnabled = settings?.IsPostPaidActive;
  const isIRAAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.IRA;
  const isUtmaAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.UTMA;
  const isDeliveryOnlyAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.Basic;

  const isPostPaidVisible = !isIRAAccount && isPostPaidEnabled;
  const [wizardTitle, setWizardTitle] = useState('Buy');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    wizardData?.selectedPaymentMethod || null,
  );
  const [goBackHandler, setGoBackHandler] = useState(null);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  useEffect(() => {
    if (isBuyStorageSuccessful || errorMessage) {
      setWizardData({
        ...wizardData,
        isBuyStorageSuccessful: !isBuyStorageSuccessful,
        errorMessage,
        isSegregated,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuyStorageSuccessful, errorMessage]);

  const setDataForWizard = (value) => {
    setWizardData((prev) => ({ ...prev, ...value }));
    storeInSessionStorage(WIZARD_DATA, { ...wizardData, ...value });
  };

  useEffect(() => {
    if (isEmpty(wizardData?.storageProduct)) {
      setDataForWizard({ storageProduct });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageProduct]);

  const closeModal = () => {
    handleCloseModal();
    removeFromSessionStorage(WIZARD_DATA);
    removeFromSessionStorage(STATUS_NEW_ADDRESS_ADDED);
    removeFromSessionStorage(SIGNIFYD_SESSION_ID);
    removeFromSessionStorage(UPGRADE_DELIVERY_ONLY_ACCOUNT);
  };

  const handleBackButtonClick = () => {
    if (goBackHandler) {
      goBackHandler();
    }
  };

  const closeSupport = () => {
    setIsSupportOpen(false);
    // setModalSize('sm');
  };

  const openSupport = () => {
    setIsSupportOpen(true);
    // setModalSize('lg');
  };

  // eslint-disable-next-line no-nested-ternary
  const goBackFn = isSupportOpen
    ? closeSupport
    : goBackHandler
    ? handleBackButtonClick
    : undefined;

  return (
    <BasicModal
      title={wizardTitle}
      size="lg"
      isOpen={isModalOpen}
      close={closeModal}
      closeIconVisible
      goBack={goBackFn}
    >
      <StepByStep sessionScopeStep={BUY_FOR_STORAGE_WIZARD}>
        {({ goStepForward, goStepBack, skipPreviousStep, currentStepIndex }) =>
          BuyFlowSteps.map((step, index) =>
            React.createElement(step.children, {
              // key: index,
              stepIndex: index,
              handleBack: goStepBack,
              handleNext: goStepForward,
              skipPreviousStep,
              closeModal,
              setWizardData: setDataForWizard,
              wizardData,
              handleCloseModalOnBuy,
              hasError: wizardData?.isBuyStorageSuccessful,
              text: wizardData?.errorMessage || (
                <>
                  <Paragraph centerText>
                    {t('buyWizard.confirmationMessage.orderPlaced')}
                  </Paragraph>
                  <Paragraph centerText>
                    {t('buyWizard.confirmationMessage.onTradeConfirmedMessage')}
                  </Paragraph>
                </>
              ),
              onButtonClick: closeModal,
              setWizardTitle,
              isProductPage,
              skipChooseLocation,
              tiers,
              counter,
              unity,
              isSegregated,
              isPostPaidVisible,
              selectedPaymentMethod,
              setSelectedPaymentMethod,
              setGoBackHandler,
              currentStepIndex,
              isGS,
              openSupport,
              closeSupport,
              isSupportOpen,
              isUtmaAccount,
              isIRAAccount,
              isDeliveryOnlyAccount,
              isInstaVaultProduct,
            }),
          )
        }
      </StepByStep>
    </BasicModal>
  );
};

BuyFlow.propTypes = {
  handleCloseModal: PropTypes.func,
  // setWizardData: PropTypes.func,
  wizardData: PropTypes.shape({
    isBuyStorageSuccessful: PropTypes.bool,
    errorMessage: PropTypes.string,
    isSegregated: PropTypes.bool,
  }),
  // setWizardTitle: PropTypes.func,
  // setCloseIconVisible: PropTypes.func,
  isProductPage: PropTypes.bool,
  skipChooseLocation: PropTypes.bool,
  isSegregated: PropTypes.bool,
  handleCloseModalOnBuy: PropTypes.func,
  tiers: PropTypes.shape({
    HighBound: PropTypes.number,
    LowBound: PropTypes.number,
    PricePerOz: PropTypes.number,
    PricePerUnit: PropTypes.number,
  }),
  counter: PropTypes.number,
  unity: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  isInstaVaultProduct: PropTypes.bool,
};

export default BuyFlow;
